import { ChangeEvent, useState } from "react";

import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";

import {
  getServiceOrderTypeValuesByType,
  serviceOrderTypeValues,
} from "@/_constants/serviceOrder";
import { tenantIcon, tenantTitle } from "@/_constants/tenantType";
import { displayMinName, getDateFromIso } from "@/_helpers/stringHelper";
import ColoredMarker from "@/common/components/ColoredMarker";
import GridActionButtons from "@/common/components/GridActionButtons";
import Loader from "@/common/components/Loader";
import ModalDwellingExpress from "@/common/components/ModalDwellingExpress";
import NewDPE from "@/common/components/NewDPEComponent";
import ServiceTypeMinComponent from "@/common/components/ServiceTypeMinComponent";
import Status from "@/common/components/StatusComponent";
import { GridType, Permissions } from "@/common/enums/sharedEnums";
import { LazyTableState } from "@/common/interfaces";
import { BUILDING_DETAILS } from "@/modules/building/routes";
import DwellingDataGridBadgeComponent from "@/modules/dwelling/components/DwellingDataGridBadgeComponent";
import { IDwelling, IResponsePaginateServices } from "@/modules/dwelling/reducers";
import { DWELLING_DETAILS } from "@/modules/dwelling/routes";
import { deleteDwelling, getDwellings } from "@/modules/dwelling/services";
import { ITenant } from "@/modules/serviceOrdering/reducers";
import { SERVICE_ORDERING } from "@/modules/serviceOrdering/routes/ServiceOrderingRoutes";
import { useServiceInventoryStore } from "@/modules/serviceOrdering/store/ServiceInventoryStore";
import history from "@/routes/history";


const DwellingsComponent = () => {
  const queryClient = useQueryClient();

  const addressFilter = useServiceInventoryStore((state) => state.address);
  const setAddressFilter = useServiceInventoryStore(
    (state) => state.setAddressFilter
  );
  const setSelectedAddress = useServiceInventoryStore(
    (state) => state.setSelectedAddress
  );

  const { setShowModalDwellingExpress, showModalDwellingExpress } =
    useServiceInventoryStore((state) => state);

  const [editId, setEditId] = useState<string>();

  const defaultLazyData: LazyTableState = {
    first: 0,
    rows: 10,
    page: 0,
    filter: "",
    filters: {
      agency: { value: null, matchMode: "startsWith" },
      managementLotNumber: { value: null, matchMode: "startsWith" },
      condominiumNumber: { value: null, matchMode: "startsWith" },
      taxNumber: { value: null, matchMode: "startsWith" },
      building: { value: null, matchMode: "startsWith" },
      category: { value: null, matchMode: "startsWith" },
      address: { value: addressFilter, matchMode: "startsWith" },
      lessor: { value: null, matchMode: "startsWith" },
      tenant: { value: null, matchMode: "startsWith" },
      leaseStartDate: { value: null, matchMode: "dateIs" },
      leaseEndDate: { value: null, matchMode: "dateIs" },
      lastServiceType: { value: null, matchMode: "equals" },
      lastMeasurementDate: { value: null, matchMode: "dateIs" },
      dpe: { value: null, matchMode: "equals" },
      goodCondition: { value: null, matchMode: "startsWith" },
      cleanliness: { value: null, matchMode: "startsWith" },
    },
  };
  const [lazyState, setLazyState] = useState<LazyTableState>(defaultLazyData);

  const { data, isLoading, error, isFetching } = useQuery<
    IResponsePaginateServices,
    Error
  >(["dwellings", lazyState], () => getDwellings(lazyState), {
    keepPreviousData: true,
  });

  const removeDwelling = useMutation({
    mutationFn: (idDwelling: string) => deleteDwelling(idDwelling),
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de la suppression du bien");
    },
    onSuccess: async () => {
      toast.success("Le bien a été supprimé avec succès");
      queryClient.invalidateQueries({ queryKey: ["dwellings", lazyState] });
    },
  });

  if (error) {
    console.log("Error :", error);
    return (
      <Message
        severity="error"
        text={
          "Erreur: Impossible de récupérer la liste de mes commandes.\nVeuillez contacter votre administrateur système."
        }
      />
    );
  }

  const lastAction = (item: IDwelling) => {
    return item?.lastService?.type
      ? getServiceOrderTypeValuesByType(item?.lastService?.type as keyof object)
          ?.labelMin
      : "Aucune";
  };

  const onPageHandler = (event: DataTablePageEvent) =>
    setLazyState((prev) => {
      return { ...prev, ...event };
    });

  const onSortHandler = (event: DataTableSortEvent) =>
    setLazyState((prev) => {
      return { ...prev, multiSortMeta: event?.multiSortMeta || [] };
    });

  const onFilterHandler = (event: DataTableFilterEvent) =>
    setLazyState((prev) => {
      setAddressFilter(event.filters.address?.value);
      return { ...prev, filters: event.filters };
    });

  const clearFilterHandler = () => setLazyState(defaultLazyData);

  const typeRowFilterTemplate = (
    options: ColumnFilterElementTemplateOptions
  ) => {
    return (
      <Dropdown
        value={options.value}
        options={Object.values(serviceOrderTypeValues).map((el) => ({
          label: el.labelMin,
          value: el.name,
        }))}
        onChange={(e) => options.filterApplyCallback(e.value)}
        itemTemplate={(option) => (
          <ServiceTypeMinComponent type={option.value} />
        )}
        placeholder="Prestations"
        className="p-column-filter"
        showClear
      />
    );
  };

  const dpeRowFilterTemplate = (
    options: ColumnFilterElementTemplateOptions
  ) => {
    return (
      <Dropdown
        value={options.value}
        options={["A", "B", "C", "D", "E", "F", "G"].map((el) => el)}
        onChange={(e) => options.filterApplyCallback(e.value)}
        itemTemplate={(option) => <NewDPE params={{ dpe: option }} />}
        placeholder="DPE"
        className="p-column-filter"
        showClear
      />
    );
  };

  const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Calendar
        value={options.value}
        onChange={(e) => options.filterApplyCallback(e.value, options.index)}
        dateFormat="dd/mm/yy"
        placeholder="jj/mm/aaaa"
      />
    );
  };

  return (
    <div className="d-flex flex-column gap-3">
      <ModalDwellingExpress
        show={showModalDwellingExpress || editId !== undefined}
        id={editId}
        onHide={() => {
          setShowModalDwellingExpress(false);
          setEditId(undefined);
        }}
        onSubmit={(dwelling) => {
          setSelectedAddress(dwelling);
          queryClient.invalidateQueries({ queryKey: ["dwellings", lazyState] });
        }}
      />
      <h1>Mes biens</h1>
      <div className="my-3">
        <Button
          onClick={() => {
            setShowModalDwellingExpress(true);
          }}
          type="button"
          severity="success"
          className="btn-add-new btn-success"
        >
          <i className="bi bi-plus" />
          Ajouter un bien
        </Button>
      </div>
      <div className="d-flex justify-content-between gap-3">
        <div className="p-input-icon-left w-100">
          <i className="pi pi-search" />
          <InputText
            className="w-100"
            value={lazyState.filter}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setLazyState((prev) => {
                return { ...prev, filter: e.target.value };
              })
            }
            placeholder="Rechercher le bien par adresse, bailleur ou nom de l’ancien locataire..."
          />
        </div>
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Effacer"
          onClick={clearFilterHandler}
        />
      </div>
      <DataTable
        value={data?.dwellings || []}
        loading={isLoading || isFetching}
        loadingIcon={<Loader />}
        lazy
        size={"small"}
        removableSort
        sortMode="multiple"
        multiSortMeta={lazyState.multiSortMeta}
        filterDisplay="row"
        filters={lazyState.filters}
        onPage={onPageHandler}
        onSort={onSortHandler}
        onFilter={onFilterHandler}
        paginator
        first={lazyState.first}
        rows={lazyState.rows}
        totalRecords={data?.total}
        rowsPerPageOptions={[10, 20, 30]}
        emptyMessage={"Aucun résultat trouvé :/"}
        scrollable
      >
        <Column
          style={{ minWidth: "150px" }}
          header="Agence"
          field="agency"
          body={(data: IDwelling) => data.agency.label}
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Bailleur"
          field="lessor"
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="N° de lot de gestion"
          field="managementLotNumber"
          body={(data: IDwelling) =>
            data?.managementLotNumber ? (
              <Link
                to={DWELLING_DETAILS.replace(
                  ":id",
                  data.datedReference || data.id || ""
                )}
                className="propertyAdressLink  link-td text-center d-flex flex-row justify-content-center gap-2"
              >
                {data.managementLotNumber}
              </Link>
            ) : (
              "-"
            )
          }
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="N° de lot Copropriété"
          field="condominiumNumber"
          body={(data: IDwelling) =>
            data?.condominiumNumber ? (
              <Link
                to={DWELLING_DETAILS.replace(
                  ":id",
                  data.datedReference || data.id || ""
                )}
                className="propertyAdressLink  link-td text-center d-flex flex-row justify-content-center gap-2"
              >
                {data.condominiumNumber}
              </Link>
            ) : (
              "-"
            )
          }
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "250px" }}
          header="Adresse"
          field="address"
          body={(data: IDwelling) =>
            data?.address ? (
              <DwellingDataGridBadgeComponent
                dwelling={data}
                linkEnable={data.permissions.includes(
                  Permissions.BUILDING_DWELLING_GET
                )}
              />
            ) : (
              "-"
            )
          }
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Occupant(s)"
          field="tenant"
          body={(data: IDwelling) => {
            const allTenants = [
              ...(data?.previousTenants || []),
              ...(data?.currentTenants || []),
            ];

            if (!allTenants || allTenants.length === 0) {
              return "-";
            }

            return allTenants?.map((tenant: ITenant, index: number) => {
              return (
                <div
                  key={index}
                  className="d-flex gap-2"
                  data-toggle="tooltip"
                  data-placement="top"
                  title={tenantTitle(tenant.type)}
                >
                  <div className="fw-bold">
                    {displayMinName(
                      tenant?.person?.lastname,
                      tenant?.person?.firstname
                    )}
                  </div>
                  <i className={tenantIcon(tenant.type)}></i>
                </div>
              );
            });
          }}
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Signature de bail"
          dataType="date"
          field="leaseStartDate"
          body={(data: IDwelling) => {
            const allTenants = [
              ...(data?.previousTenants || []),
              ...(data?.currentTenants || []),
            ];

            if (!allTenants || allTenants.length === 0) {
              return "-";
            }

            return (
              <div>
                {allTenants?.map((person: ITenant, index: number) => {
                  const leaseStartDate = person.rentalStartDate ?? null;
                  const startDateText = leaseStartDate
                    ? getDateFromIso(leaseStartDate)
                    : "-";
                  return <div key={index}>{startDateText}</div>;
                })}
              </div>
            );
          }}
          sortable
          filter
          filterElement={dateFilterTemplate}
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Fin du bail"
          field="leaseEndDate"
          body={(data: IDwelling) => {
            const allTenants = [
              ...(data?.previousTenants || []),
              ...(data?.currentTenants || []),
            ];

            if (!allTenants || allTenants.length === 0) {
              return "-";
            }

            return (
              <div>
                {allTenants?.map((person: ITenant, index: number) => {
                  const leaseEndDate = person.rentalEndDate ?? null;
                  const endDateText = leaseEndDate
                    ? getDateFromIso(leaseEndDate)
                    : "-";
                  return <div key={index}>{endDateText}</div>;
                })}
              </div>
            );
          }}
          sortable
          filter
          filterElement={dateFilterTemplate}
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Dernière action"
          field="lastServiceType"
          body={(data: IDwelling) => (
            <Status className="mx-auto" currentStatus={lastAction(data)} />
          )}
          sortable
          filter
          filterElement={typeRowFilterTemplate}
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="DPE"
          field="dpe"
          body={(data: IDwelling) =>
            data?.dpe ? (
              <NewDPE params={{ dpe: data.dpe }}></NewDPE>
            ) : (
              <ColoredMarker className="mx-auto" color="#000D5B55">
                X
              </ColoredMarker>
            )
          }
          filter
          filterElement={dpeRowFilterTemplate}
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Etat (bon et +)"
          field="goodCondition"
          body={(data: IDwelling) =>
            data?.goodCondition !== null ? data?.goodCondition + "%" : "-"
          }
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Propreté"
          field="cleanliness"
          body={(data: IDwelling) =>
            data?.cleanliness !== null ? data?.cleanliness + "%" : "-"
          }
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Compteur eau"
          field="coldWaterDate"
          body={(data: IDwelling) => {
            const lastMeasurementDate = data?.coldWater?.createdAt ?? null;
            return lastMeasurementDate
              ? getDateFromIso(lastMeasurementDate)
              : "-";
          }}
          sortable
          filter
          filterElement={dateFilterTemplate}
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Type de bien"
          field="category"
          body={(data: IDwelling) => data?.category?.label}
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          header="Type de location"
          field="isFurnished"
          body={(data: IDwelling) =>
            data.isFurnished ? "Location meublée" : "Location nue"
          }
          sortable
          filter
          filterElement={
            <Dropdown
              value={lazyState?.filters?.isFurnished}
              options={[
                { label: "Tous", value: null },
                { label: "Location meublée", value: true },
                { label: "Location nue", value: false },
              ]}
              onChange={(e) =>
                setLazyState((prev) => ({
                  ...prev,
                  filters: {
                    ...prev.filters,
                    isFurnished: { value: e.value, matchMode: "equals" },
                  },
                }))
              }
              placeholder="Sélectionner"
              className="p-column-filter"
              showClear
            />
          }
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Invariant fiscal"
          field="taxNumber"
          body={(data: IDwelling) =>
            data?.taxNumber ? (
              <Link
                to={DWELLING_DETAILS.replace(
                  ":id",
                  data.datedReference || data.id || ""
                )}
                className="propertyAdressLink  link-td text-center d-flex flex-row justify-content-center gap-2"
              >
                {data.taxNumber}
              </Link>
            ) : (
              "-"
            )
          }
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Immeuble"
          field="building"
          body={(data: IDwelling) =>
            data?.building?.address.residenceName ? (
              <Link
                to={BUILDING_DETAILS.replace(
                  ":id",
                  data.building.datedReference || data.building.id || ""
                )}
                className="propertyAdressLink  link-td text-center d-flex flex-row justify-content-center gap-2"
              >
                {data.building.address.residenceName}
              </Link>
            ) : (
              "-"
            )
          }
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          header="Actions"
          frozen
          alignFrozen="right"
          body={(data: IDwelling) => (
            <div>
              <GridActionButtons
                order={() => {
                  setSelectedAddress(data);
                  setAddressFilter("");
                  history.push(SERVICE_ORDERING);
                }}
                {...(data.permissions.includes(
                  Permissions.BUILDING_DWELLING_GET
                ) && {
                  viewUrl: DWELLING_DETAILS.replace(
                    ":id",
                    data.datedReference || data.id || ""
                  ),
                  downloadablePath: data?.mainFile?.path,
                })}
                {...(data.permissions.includes(
                  Permissions.BUILDING_DWELLING_UPDATE
                ) && {
                  onClickEdit: () => {
                    const id = data.datedReference || data.id || null;
                    id && setEditId(id);
                  },
                })}
                {...(data.permissions.includes(
                  Permissions.BUILDING_DWELLING_UPDATE
                ) && {
                  remove: () => removeDwelling.mutate(data.id),
                })}
                type={GridType.dwelling}
              />
            </div>
          )}
        ></Column>
      </DataTable>
    </div>
  );
};
export default DwellingsComponent;
